<template>
  <div class="container py-5">

    <div class="level">
      <button
        class="button is-primary level-left"
        @click="addNewPolicy()">
        Create a New Policy
      </button>
      
      <div class="level-right">
        <div class="flex flex--row flex--align-center mr-3">
          <label class="label mr-2 mb-0">Policy</label>
          <v-select
            v-if="policies.length > 0"
            :options="policies"
            item-text="name"
            item-value="id"
            @input="selectPolicy"
            :value="policy.id"
          />     
        </div>
        <div class="level-right buttons">
          <button
            :disabled="!policy.id"
            class="button is-primary" 
            @click.stop="saveChanges()">
            Save
          </button>
          <button
            :disabled="!policy.id"
            class="button is-secondary" 
            @click.stop="copyPolicy()">
            <span class="icon">
              <i class="fas fa-copy"></i>
            </span>
            <span>Copy</span>
          </button>
          <button
            :disabled="!policy.id"
            @click="editingPolicy = true"
            class="button is-secondary">
            <span class="icon">
              <i class="fas fa-edit"></i>
            </span>
            <span>Edit</span>
          </button>
          <button
            :disabled="!policy.id"
            @click.stop="removePolicy()" 
            class="button">
            <span class="icon">
              <i class="fas fa-trash has-text-grey"></i>
            </span>
            <span>Delete</span>
          </button>
          <button
            @click.stop="clear()" 
            class="button is-secondary">
            <span class="icon">
              <i class="fas fa-sync-alt"></i>
            </span>
            <span>Refresh</span>
          </button>
        </div>
      </div>      
    </div>

    <div class="level">
      <div class="level-left title is-4 mr-3 ml-1 my-2">
        Permissions
      </div>
    </div>

    <v-fade-transition mode="out-in">
      <v-alert 
        class="my-3"
        v-if="!loading && permissions.length == 0"
        type="info">
        There are no permissions.
      </v-alert>

      <table v-else class="table is-fullwidth">
        <thead>
          <tr>
            <template
              v-for="header in headers">
              <td
                v-if="header.isClickable"
                class="tab-header"
                :class="{'active-tab' : activeTab == header.label}"
                :colspan="header.colspan"
                :key="'header-' + header.label"
                @click="setActiveTab(header.label)">
                <strong>{{ header.label }}</strong>
              </td>
              <td
                v-else
                :colspan="header.colspan"
                :key="'header-' + header.label">
                <strong>{{ header.label }}</strong>
              </td>
            </template>
          </tr>
          <tr v-if="loading">
            <th :colspan="headersTotalLength()" class="py-0 px-0 is-naked">
              <v-progress class="is-fullwidth" v-model="loading"/>
            </th>
          </tr>
        </thead>

        <tbody>
          <template v-for="(subMods, mod) in permissions">
            <template v-for="(perms, subMod, j) in subMods">
              <tr :key="subMod">
                <td v-if="j == 0"
                  :style="firstColumnStyle"
                  class="module"
                  :rowspan="Object.keys(subMods).length">
                  {{ mod | titlecase('_') }}
                  <v-tooltip message="Select all">
                    <span 
                      class="icon is-small is-clickable ml-2" 
                      @click="checkManyPermissions('module', mod, subMods, perms, true)">
                      <i class="far fa-check-square"></i>
                    </span>
                  </v-tooltip>
                  <v-tooltip message="Unselect all">
                    <span 
                      class="icon is-small is-clickable" 
                      @click="checkManyPermissions('module', mod, subMods, perms, false)">
                      <i class="far fa-square"></i>
                    </span>
                  </v-tooltip>
                </td>

                <td class="sub-module" 
                  :key="refreshRow">
                  {{ subMod | titlecase('_') }}
                  <v-tooltip message="Select all">
                    <span class="icon is-small is-clickable ml-2" 
                      @click="checkManyPermissions('submodule', mod, subMod, perms, true)">
                      <i class="far fa-check-square"></i>
                    </span>
                  </v-tooltip>
                  <v-tooltip message="Unselect all">
                    <span 
                      class="icon is-small is-clickable" 
                      @click="checkManyPermissions('submodule', mod, subMod, perms, false)">
                      <i class="far fa-square"></i>
                    </span>
                  </v-tooltip>
                </td>

                <td colspan="2"> 
                  <div class="flex flex--row flex--justify-space-between">
                    <div
                      class="flex--grow" 
                      v-for="(key, permission) in perms"
                      v-show="showPermission(permission)"
                      :class="permissionClass(permission)"
                      :key="permission">
                      <v-checkbox
                        :label="permission | titlecase('_')"
                        :value="permissionExists(mod, subMod, permission)"
                        @input="addPermission(mod, subMod, permission, key, $event)"/>              
                    </div>
                  </div>
                </td>
                
              </tr>
            </template>
          </template>
        </tbody>
      </table>
    </v-fade-transition>

    <add-policy-modal 
      v-model="addingPolicy"
      :permissions="policy.permissions"
      :createEmpty="addEmptyPolicy"
      @finishCreate="selectPolicy"/>

    <edit-policy-modal 
      v-model="editingPolicy"
      :policy="policy"
      @finishChanges="clearChanges"/>      

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmed ? deleteConfirmed() : null"
      @cancel="confirmDelete.state = false"/>

    <v-confirm-modal 
      v-model="confirmDiscardChanges.state"
      :title="confirmDiscardChanges.title"
      :message="confirmDiscardChanges.message"
      :type="confirmDiscardChanges.type"
      @confirm="discardConfirmed ? discardConfirmed() : null"
      @cancel="confirmDiscardChanges.state = false"/>      

    <v-confirm-modal 
      v-model="confirmCopyPolicy.state"
      :title="confirmCopyPolicy.title"
      :message="confirmCopyPolicy.message"
      :type="confirmCopyPolicy.type"
      @confirm="copyPolicyConfirmed ? copyPolicyConfirmed() : null"
      @cancel="confirmCopyPolicy.state = false"/>   

  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import VProgress from '@/components/base/VProgress'
import VAlert from '@/components/base/VAlert'
import VTooltip from '@/components/base/VTooltip'
import VFadeTransition from '@/components/transitions/VFadeTransition'
import VCheckbox from '@/components/base/VCheckbox'
import VSelect from '@/components/base/VSelect'
import AddPolicyModal from '@/components/permissions/AddPolicyModal'
import EditPolicyModal from '@/components/permissions/EditPolicyModal'
import VConfirmModal from '@/components/base/VConfirmModal'

export default {
  components: {
    VProgress,
    VAlert,
    VFadeTransition,
    VCheckbox,
    VSelect,
    AddPolicyModal,
    EditPolicyModal,
    VConfirmModal,
    VTooltip
  },
  data() {
    return {
      headers: [
       { label: 'Module', colspan: 1, isClickable: false },
       { label: 'Sub-Module', colspan: 1, isClickable: false },
       { label: 'Access', colspan: 1, isClickable: true },
       { label: 'Workflow', colspan: 1, isClickable: true }
      ],
      accessColumns: [ 'view', 'update', 'create', 'delete' ],
      activeTab: 'Access',
      policy: { 
        permissions: { }
      },
      addingPolicy: false,
      addEmptyPolicy: true,
      editingPolicy: false,
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: this.deleteDefaultMessage,
        type: 'danger'
      },
      deleteDefaultMessage: 'Are you sure you want to delete this policy?',
      deleteConfirmed: null,
      confirmDiscardChanges: {
        state: false,
        title: 'Discard Changes',
        message: 'Discard unsaved changes?',
        type: 'info'
      },
      discardConfirmed: null,
      editing: false,
      confirmCopyPolicy: {
        state: false,
        title: 'Copy Policy',
        message: 'Do you want to create a new policy based on the current policy selected?',
        type: 'info'
      },
      copyPolicyConfirmed: null,
      refreshRow: false
    }
  },
  computed: {
    ...mapState("permissions", ["loading", "permissions"]),
    ...mapState("permissions", ["policies"]),
    ...mapState("users", ["users"]),
    firstColumnStyle() {
      return { verticalAlign: 'middle' }
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("permissions", ["getAllPermissions", "updatePolicy", "deletePolicy"]),
    ...mapActions("permissions", ["getPolicies"]),
    ...mapActions("users", ["getUsers"]),
    async initialize(policyId=null) {
      this.getUsers();
      this.getAllPermissions();
      await this.getPolicies();

      if (policyId) this.policy = this.policies.find(p => p.id == policyId);
      else this.policy = this.policies[0];
    },
    showPermission(permission) {
      if (this.activeTab === 'Access' && this.accessColumns.includes(permission))
        return true;
      else if (this.activeTab !== 'Access' && !this.accessColumns.includes(permission))
        return true;
      
      return false;
    },
    setActiveTab(tabName) {
      this.activeTab = tabName || 'Access';
    },
    selectPolicy(policyId) {
      if (policyId != this.policy.id) {
        this.discardChanges(() => {
          let policy = this.policies.find(p => p.id == policyId); 
          this.policy = policy;
          this.activeTab = 'Access';
        });
      }
    },
    checkManyPermissions(level, mod, subMods, permissions, check) {
      if (level === 'submodule') {
        Object.entries(permissions).forEach(([permission, key]) => {
          this.addPermission(mod, subMods, permission, key, check);
        });
      } else {
        Object.entries(subMods).forEach(([subMod, permissions]) => {
          this.checkManyPermissions('submodule', mod, subMod, permissions, check);
        });
      }

      this.refreshRow = !this.refreshRow;
    },
    permissionExists(mod, subMod, permission) {
      if (this.policy) {
        if (this.policy.permissions[mod]) {
          if (this.policy.permissions[mod][subMod]) {
            if (this.policy.permissions[mod][subMod][permission]) {
              return true;
            }
          }
        }
      }

      return false;
    },
    addPermission(mod, subMod, permission, key, check) {
      this.editing = true;

      const setPermission = (permissions, path, value) => { 
          const keys = path.split('.');
          const lastKey = keys.pop();
          const lastObj = keys.reduce((permissions, key) => {
            return permissions[key] = permissions[key] || {};
          }, permissions);

          lastObj[lastKey] = value;
      };

      const objectIsEmpty = (obj) => {
        return Object.entries(obj).length === 0;
      }

      if (check) {
        setPermission(this.policy.permissions, `${mod}.${subMod}.${permission}`, key);
      } else {
        if (this.permissionExists(mod, subMod, permission)) {
          delete this.policy.permissions[mod][subMod][permission];
  
          if (objectIsEmpty(this.policy.permissions[mod][subMod])) delete this.policy.permissions[mod][subMod];
          if (objectIsEmpty(this.policy.permissions[mod])) delete this.policy.permissions[mod];         
        }
      }
    },
    permissionClass(permission) {
      if (!this.accessColumns.includes(permission)) return 'permission-workflow';
      else return 'permission-access';
    },
    addNewPolicy() {
      this.discardChanges(() => {
        this.addEmptyPolicy = true;
        this.addingPolicy = true;
      });
    },
    saveChanges() {
      this.updatePolicy({ policyId: this.policy.id, data: this.policy}).then(resp => {
        this.$emit('success', resp.data);
        this.editing = false;
      })
    },
    removePolicy() {
      this.deleteConfirmed = () => {
        this.deletePolicy(this.policy.id).then(resp => {
          this.$emit('success', resp.data);          
          this.confirmDelete.state = false;

          this.clear();
        });
      }

      if (this.checkPolicyInUse(this.policy.id)) {
        this.confirmDelete.message = 'This policy is already in use. ' + this.deleteDefaultMessage;
      } else {
        this.confirmDelete.message = this.deleteDefaultMessage;
      }

      this.confirmDelete.state = true;
    },
    copyPolicy() {
      this.discardChanges(() => {
        this.copyPolicyConfirmed = () => {
          this.addEmptyPolicy = false;
          this.addingPolicy = true;
          this.confirmCopyPolicy.state = false;
        }
  
        this.confirmCopyPolicy.state = true;
      });
    },
    discardChanges(callback) {
      if (this.editing) {
        this.discardConfirmed = () => {
          callback();

          this.confirmDiscardChanges.state = false;
          this.editing = false;
        }
  
        this.confirmDiscardChanges.state = true;
      } else {
        callback();
      }
    },
    clear() {
      this.discardChanges(() => {
        let currPolicyId = this.policy.id;
        this.initialize(currPolicyId);
      });
    },
    clearChanges() {
      this.editing = false;
    },
    headersTotalLength() {
      let length = 0;

      this.headers.forEach(h => { length += h.colspan; });

      return length;
    },
    checkPolicyInUse(policyId) {
      let user = null;

      user = this.users.find(u => {
        if (u.policy) return u.policy.id === policyId;
      });
      
      if (user) return true;

      return false;
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';

.table {
  tbody, tr, td {
    .tab-header {
      text-align: center;
      cursor: pointer;
    }
    
    .active-tab {
      border-bottom-width: 3px;
      border-bottom-style: solid;
      border-bottom-color:#00d1b2;
    }

    .module {
      width: 180px;
    }

    .sub-module {
      width: 300px;
    }
  }
}

.fa-check-square, .fa-square {
  &:hover {
    color:#007aff;
    cursor: pointer;
  }
}
</style>